// InMAP engine result table — 5-pollutant SR library output.
//
// Today's payload shape (from `inferno/query.py::_run_inmap`):
//   {
//     n_plants_aligned, n_receptor_cells, library_version_dims,
//     max_<species>_ug_m3, sum_<species>_ug_m3, p99_<species>_ug_m3,
//   }
// where species ∈ {PSO4, PNO3, PNH4, PM25, SOA}.
//
// The table is built defensively: any species the payload mentions
// becomes a row, and richer keys that future versions may add
// (`total_dalys`, `top_sources`, `species_breakdown`) are surfaced when
// present.

import React from "react";
import { toSigFigs } from "../../utils/format";

interface InmapEngineTableProps {
  payload: Record<string, any>;
}

const KNOWN_SPECIES = ["PSO4", "PNO3", "PNH4", "PM25", "SOA"];

interface SpeciesRow {
  species: string;
  sum: number | null;
  max: number | null;
  p99: number | null;
}

function extractSpecies(payload: Record<string, any>): SpeciesRow[] {
  const seen = new Set<string>();
  // Auto-discover any "<verb>_<species>_ug_m3" keys the payload exposes.
  for (const k of Object.keys(payload || {})) {
    const m = /^(?:max|sum|p99)_(.+)_ug_m3$/.exec(k);
    if (m) seen.add(m[1]);
  }
  // Stable ordering: known species first, then anything new.
  const ordered = [
    ...KNOWN_SPECIES.filter((s) => seen.has(s)),
    ...Array.from(seen).filter((s) => !KNOWN_SPECIES.includes(s)),
  ];
  return ordered.map((sp) => ({
    species: sp,
    sum: typeof payload[`sum_${sp}_ug_m3`] === "number" ? payload[`sum_${sp}_ug_m3`] : null,
    max: typeof payload[`max_${sp}_ug_m3`] === "number" ? payload[`max_${sp}_ug_m3`] : null,
    p99: typeof payload[`p99_${sp}_ug_m3`] === "number" ? payload[`p99_${sp}_ug_m3`] : null,
  }));
}

export function InmapEngineTable({ payload }: InmapEngineTableProps) {
  const species = extractSpecies(payload || {});
  const totalDalys = typeof payload?.total_dalys === "number" ? payload.total_dalys : null;
  const topSources = Array.isArray(payload?.top_sources) ? payload.top_sources : null;
  const speciesBreakdown =
    payload?.species_breakdown && typeof payload.species_breakdown === "object"
      ? payload.species_breakdown
      : null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Stat label="Plants aligned" value={payload?.n_plants_aligned} />
        <Stat label="Receptor cells" value={payload?.n_receptor_cells} />
        {totalDalys !== null && <Stat label="Total DALYs" value={totalDalys} />}
        {payload?.library_version_dims && (
          <Stat
            label="Library dims"
            value={Object.entries(payload.library_version_dims)
              .map(([k, v]) => `${k}=${v}`)
              .join(", ")}
          />
        )}
      </div>

      {species.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface-600">
              <tr>
                <Th>Species</Th>
                <Th align="right">Sum (μg/m³ · cells)</Th>
                <Th align="right">Max (μg/m³)</Th>
                <Th align="right">p99 (μg/m³)</Th>
                {speciesBreakdown && <Th align="right">DALYs</Th>}
              </tr>
            </thead>
            <tbody className="bg-surface-700 divide-y divide-border">
              {species.map((row) => (
                <tr key={row.species} className="hover:bg-surface-600 transition-colors">
                  <Td className="font-medium text-text-primary">{row.species}</Td>
                  <Td align="right">{row.sum === null ? "—" : toSigFigs(row.sum)}</Td>
                  <Td align="right">{row.max === null ? "—" : toSigFigs(row.max)}</Td>
                  <Td align="right">{row.p99 === null ? "—" : toSigFigs(row.p99)}</Td>
                  {speciesBreakdown && (
                    <Td align="right">
                      {typeof speciesBreakdown[row.species]?.dalys === "number"
                        ? toSigFigs(speciesBreakdown[row.species].dalys)
                        : "—"}
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {species.length === 0 && (
        <p className="text-sm text-text-muted">
          InMAP ran but no per-species concentrations were recorded for this run.
        </p>
      )}

      {topSources && topSources.length > 0 && (
        <div>
          <h5 className="text-sm font-medium text-text-primary mb-2">Top contributing source cells</h5>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-600">
                <tr>
                  <Th>Source cell</Th>
                  <Th align="right">Contribution</Th>
                </tr>
              </thead>
              <tbody className="bg-surface-700 divide-y divide-border">
                {topSources.slice(0, 10).map((src: any, idx: number) => (
                  <tr key={idx} className="hover:bg-surface-600 transition-colors">
                    <Td>{String(src?.cell ?? src?.id ?? src?.label ?? idx)}</Td>
                    <Td align="right">
                      {typeof src?.value === "number" ? toSigFigs(src.value) : "—"}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: any }) {
  return (
    <div className="bg-surface-800 rounded p-3">
      <div className="text-xs text-text-muted uppercase tracking-wider">{label}</div>
      <div className="text-sm text-text-primary mt-1">
        {value === null || value === undefined
          ? "—"
          : typeof value === "number"
          ? toSigFigs(value)
          : String(value)}
      </div>
    </div>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={`px-4 py-2 text-xs font-medium text-text-muted uppercase tracking-wider ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  className = "",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <td
      className={`px-4 py-2 text-sm text-text-secondary ${
        align === "right" ? "text-right" : "text-left"
      } ${className}`}
    >
      {children}
    </td>
  );
}
